// @ai-generated 2026-09-28T12:20:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
import { useRepository, type RepositoryConstructor } from '@Hooks/useRepository';
import {
    EnumFilterExpressionOperation,
    type FilterCondition,
    type FilterExpression,
    type IServerListApi,
    type ListInfo,
    type ListRequest,
    type ListRequestWithIds,
    type Result,
    unwrapOr,
} from '@synqo/frontend-component-library';
import { useMemo } from 'react';

/** The paged query every generated resource repository carries. */
export interface PagedEndpoint<TItem> {
    getListPost(listRequest: ListRequest, include: string | undefined): Promise<Result<ListInfo<TItem>>>;
}

/** The paged query and the by-id query, which a resource with a batch route carries as well. */
export interface ListEndpoints<TItem> extends PagedEndpoint<TItem> {
    getBatch(listRequest: ListRequestWithIds, include: string | undefined): Promise<Result<ListInfo<TItem>>>;
}

/** The paged query of a resource that lives under a parent, such as the orders of one requisition. */
export interface ChildListEndpoint<TItem> {
    getListPost(rootObjectId: string, listRequest: ListRequest): Promise<Result<ListInfo<TItem>>>;
}

/** The paged query of a link list, such as the groups one user is in. */
export interface LinkedListEndpoint<TItem> {
    getLinkedObjects(listRequest: ListRequest, rootObjectId: string): Promise<Result<ListInfo<TItem>>>;
}

/** What a failed page reads as, which is what the table already shows for a failure. */
export const emptyPage = <TItem>(): ListInfo<TItem> => ({
    items: [],
    total_item_count: 0,
    page_count: 0,
    page_no: 0,
    page_size: 0,
});

/** The request narrowed by one more condition, which holds on top of whatever it asked for already. */
// @ai-generated 2026-10-02T11:30:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export const withCondition = (
    listRequest: ListRequest,
    condition: FilterCondition | FilterExpression,
): ListRequest => ({
    ...listRequest,
    filter_columns: {
        operation: EnumFilterExpressionOperation.And,
        filters: listRequest.filter_columns ? [listRequest.filter_columns, condition] : [condition],
    },
});

/**
 * A table's data source, read through the generated repository of the resource it lists.
 *
 * The item type comes from the repository rather than from the caller, so a table cannot name a type
 * the endpoint does not answer with. A failed page is reported as an empty one, which is what the
 * table already does with a failure.
 */
export function useListApi<TItem>(
    repository: RepositoryConstructor<ListEndpoints<TItem>>,
    baseUrl: string,
    include: string[] = [],
): IServerListApi<TItem> {
    const api = useRepository(repository, baseUrl);
    const includes = include.join(',');

    return useMemo(
        () => ({
            loadPage: async (listRequest: ListRequest) =>
                unwrapOr(await api.getListPost(listRequest, includes), emptyPage<TItem>()),
            loadBatch: async (listRequest: ListRequestWithIds) =>
                unwrapOr(await api.getBatch(listRequest, includes), emptyPage<TItem>()),
        }),
        [api, includes],
    );
}

/**
 * The same for a resource whose controller offers no batch route.
 */
export function usePagedListApi<TItem>(
    repository: RepositoryConstructor<PagedEndpoint<TItem>>,
    baseUrl: string,
    include: string[] = [],
): Pick<IServerListApi<TItem>, 'loadPage'> {
    const api = useRepository(repository, baseUrl);
    const includes = include.join(',');

    return useMemo(
        () => ({
            loadPage: async (listRequest: ListRequest) =>
                unwrapOr(await api.getListPost(listRequest, includes), emptyPage<TItem>()),
        }),
        [api, includes],
    );
}

/**
 * The same for a resource that lives under a parent, which the parent's id selects.
 */
export function useChildListApi<TItem>(
    repository: RepositoryConstructor<ChildListEndpoint<TItem>>,
    baseUrl: string,
    rootObjectId: string,
): Pick<IServerListApi<TItem>, 'loadPage'> {
    const api = useRepository(repository, baseUrl);

    return useMemo(
        () => ({
            loadPage: async (listRequest: ListRequest) =>
                unwrapOr(await api.getListPost(rootObjectId, listRequest), emptyPage<TItem>()),
        }),
        [api, rootObjectId],
    );
}

/**
 * The same for a link list, whose members are attached to the parent rather than owned by it.
 */
export function useLinkedListApi<TItem>(
    repository: RepositoryConstructor<LinkedListEndpoint<TItem>>,
    baseUrl: string,
    rootObjectId: string,
): Pick<IServerListApi<TItem>, 'loadPage'> {
    const api = useRepository(repository, baseUrl);

    return useMemo(
        () => ({
            loadPage: async (listRequest: ListRequest) =>
                unwrapOr(await api.getLinkedObjects(listRequest, rootObjectId), emptyPage<TItem>()),
        }),
        [api, rootObjectId],
    );
}
